// ============================================
// P3 支付线（⑥边缘 License）：设备授权页
// - 档位目录（UBS license_tiers，经 /pay/license-tiers）
// - 输入设备序列号 → 查询当前 License（到期时间/能力位）
// - 购买/续费 → 全局支付弹窗（orderType=license，金额以档位为准）
// - 到期宽限期 7 天（客户端校验侧）
// ============================================
import { useCallback, useEffect, useState } from 'react';
import { Card, Button, Tag, Typography, Space, Empty, Spin, message, Row, Col, Input, Descriptions, Alert } from 'antd';
import { ShoppingCartOutlined, ReloadOutlined, SafetyCertificateOutlined, SearchOutlined } from '@ant-design/icons';
import { fetchLicenseTiers, type PayLicenseTier } from '../api/pay';
import client from '../api/client';
import { usePayIntentStore } from '../components/payment/payIntent';

const { Text, Paragraph } = Typography;

interface LicenseInfo {
  deviceSn: string;
  tenantId: string;
  features: string[];
  issuedAt: number;
  expiresAt: number;
}

const FEATURE_LABEL: Record<string, string> = {
  full_3d: '3D 场景',
  ai: 'AI',
  gis: 'GIS',
  plc_full: 'PLC 全功能',
  plugins: '插件',
};

export default function LicenseRenewal() {
  const openPay = usePayIntentStore((s) => s.openPay);
  const [tiers, setTiers] = useState<PayLicenseTier[]>([]);
  const [loading, setLoading] = useState(false);
  const [deviceSn, setDeviceSn] = useState('');
  const [license, setLicense] = useState<LicenseInfo | null>(null);
  const [querying, setQuerying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchLicenseTiers();
      setTiers(res.items || []);
    } catch (e: any) {
      message.error(`授权档位加载失败：${e?.response?.data?.message || e?.message || ''}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const query = useCallback(async (sn?: string) => {
    const target = (sn ?? deviceSn).trim();
    if (!target) { message.warning('请输入设备序列号'); return; }
    setQuerying(true);
    try {
      const { data } = await client.get(`/pay/license/${encodeURIComponent(target)}`);
      setLicense((data as any)?.license ?? null);
      if (!(data as any)?.license) message.info('该设备暂无有效 License，可直接购买');
    } catch (e: any) {
      setLicense(null);
      message.error(`查询失败：${e?.response?.data?.message || e?.message || ''}`);
    } finally {
      setQuerying(false);
    }
  }, [deviceSn]);

  const onBuy = (t: PayLicenseTier) => {
    const sn = deviceSn.trim();
    if (!sn) { message.warning('请先输入设备序列号'); return; }
    openPay(
      { orderType: 'license', productRef: t.code, amount: Number(t.price), deviceSn: sn, subject: `设备授权-${t.name}（${sn}）` },
      () => { void query(sn); },
    );
  };

  const expireText = license
    ? new Date(license.expiresAt).toLocaleString('zh-CN', { hour12: false })
    : '';

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0 }}>设备授权</h2>
        <Button icon={<ReloadOutlined />} onClick={() => void load()} data-testid="license-refresh">刷新</Button>
      </Space>
      <Paragraph type="secondary" style={{ marginTop: 8 }}>
        边缘设备按台按年授权（一次性授权基础软件服务，可选能力档位）；到期后提供 7 天宽限期，续费自当前到期日顺延。
      </Paragraph>

      <Space.Compact style={{ width: '100%', maxWidth: 560, marginBottom: 16 }}>
        <Input
          placeholder="输入设备序列号（如 ATK1126B0000001）"
          value={deviceSn}
          onChange={(e) => setDeviceSn(e.target.value)}
          data-testid="license-device-input"
          allowClear
        />
        <Button type="primary" icon={<SearchOutlined />} loading={querying} onClick={() => void query()} data-testid="license-query-btn">
          查询
        </Button>
      </Space.Compact>

      {license && (
        <div data-testid="license-current" style={{ marginBottom: 16 }}>
          <Alert
            type={license.expiresAt > Date.now() ? 'success' : 'warning'}
            showIcon
            message={`设备 ${license.deviceSn} 授权${license.expiresAt > Date.now() ? '生效中' : '已到期（宽限期内可续费）'}`}
            description={
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="到期时间">{expireText}</Descriptions.Item>
                <Descriptions.Item label="能力位">
                  {license.features?.length
                    ? license.features.map((f) => <Tag key={f} color="blue">{FEATURE_LABEL[f] || f}</Tag>)
                    : <Text type="secondary">基础软件服务</Text>}
                </Descriptions.Item>
              </Descriptions>
            }
          />
        </div>
      )}

      <Spin spinning={loading}>
        {tiers.length === 0 && !loading ? (
          <Empty description="暂无可购授权档位" />
        ) : (
          <Row gutter={[16, 16]}>
            {tiers.map((t) => (
              <Col key={t.code} xs={24} sm={12} lg={8}>
                <Card
                  size="small"
                  title={<Space><SafetyCertificateOutlined />{t.name}</Space>}
                  data-testid={`license-tier-${t.code}`}
                >
                  <Paragraph type="secondary" style={{ minHeight: 42 }}>{t.description}</Paragraph>
                  <Space orientation="vertical" size={6} style={{ width: '100%' }}>
                    <Text strong style={{ fontSize: 18, color: '#1677ff' }}>
                      ¥{Number(t.price).toFixed(2)}
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>/{t.unitLabel}</Text>
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      能力位：{t.features?.length ? t.features.map((f) => FEATURE_LABEL[f] || f).join(' / ') : '基础软件服务'} · {t.durationDays} 天
                    </Text>
                    <Button
                      type="primary" block icon={<ShoppingCartOutlined />}
                      onClick={() => onBuy(t)}
                      data-testid={`license-buy-${t.code}`}
                    >
                      购买 / 续费
                    </Button>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Spin>
    </div>
  );
}
